import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const StockActionModal = ({
  open,
  onClose,
  action,
  onSave,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState('');
  const [stockError, setStockError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setQuantity('');
      setStockError('');
      setIsDirty(false);
      setIsValid(false);
    } else {
      // Reset when opening
      setQuantity('');
      setStockError('');
      setIsDirty(false);
      setIsValid(false);
    }
  }, [open]);

  const handleQuantityChange = (e) => {
    const value = e.target.value.trim();
    setIsDirty(true);
    setQuantity(value);

    if (!value) {
      setIsValid(false);
      setStockError('Quantity is required');
      return;
    }

    if (!/^\d+$/.test(value)) {
      setIsValid(false);
      setStockError('Quantity must be a whole number');
      return;
    }

    setIsValid(true);
    setStockError('');
  };

  const handleSave = async () => {
    if (onSave && isValid && quantity) {
      // onSave is now async and will handle closing the modal after completion
      await onSave(Number(quantity));
    }
  };

  const getModalTitle = () => {
    switch (action) {
      case 'remove':
        return t('Remove Stock');
      case 'add':
        return t('Add Stock');
      case 'adjust':
        return t('Adjust Stock');
      default:
        return t('Stock Action');
    }
  };

  const getModalLabel = () => {
    switch (action) {
      case 'remove':
        return t('Remove Quantity');
      case 'add':
        return t('Add Quantity');
      case 'adjust':
        return t('Adjust Quantity');
      default:
        return t('Quantity');
    }
  };

  const getButtonText = () => {
    switch (action) {
      case 'remove':
        return t('Remove');
      case 'add':
        return t('Add');
      case 'adjust':
        return t('Adjust');
      default:
        return t('Save');
    }
  };

  if (!action) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>{getModalTitle()}</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 3, position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 1,
                borderRadius: 1,
              }}
            >
              <CircularProgress size={'32px'} />
            </Box>
          )}
          <TextField
            size="small"
            name="quantity"
            autoFocus
            fullWidth
            label={getModalLabel()}
            type="text"
            variant="outlined"
            value={quantity}
            onChange={handleQuantityChange}
            error={!!stockError}
            helperText={stockError || t('Enter a whole number')}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            InputLabelProps={{
              shrink: !!quantity,
            }}
            sx={{
              minHeight: '64px',
            }}
            disabled={loading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          sx={{ borderRadius: 1.5 }}
          disabled={loading}
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isDirty || !isValid || loading}
          color="primary"
          variant="contained"
          sx={{ borderRadius: 1.5, ml: 1 }}
        >
          {getButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockActionModal;
